import React, { useState, useCallback } from 'react';
import { Sun, Moon, Sunrise } from 'lucide-react';

export interface ColorSettingSliderProps {
  value: number;                    // 2700-6500 (color temperature in Kelvin)
  onChange: (value: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  label?: string;
  showValue?: boolean;
  showPresets?: boolean;
}

/**
 * ColorSetting Slider Control
 * 
 * 色溫滑桿控制
 * - 2700K = 暖光 (偏黃)
 * - 4000K = 日光 (中性白)
 * - 6500K = 冷光 (偏藍)
 * 
 * MCP Command: ColorAbsolute
 * Parameters: { color: { temperature: 2700-6500 } }
 */
export const Style01_Slider: React.FC<ColorSettingSliderProps> = ({
  value,
  onChange,
  disabled = false,
  min = 2700,
  max = 6500,
  label = '色溫',
  showValue = true,
  showPresets = true,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);

  // Sync local value with prop when not dragging
  React.useEffect(() => {
    if (!isDragging) {
      setLocalValue(value);
    }
  }, [value, isDragging]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    setLocalValue(newValue);
  }, []);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    onChange(localValue);
  }, [localValue, onChange]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    onChange(localValue);
  }, [localValue, onChange]);

  // Temperature to color mapping
  const getColorForTemp = (temp: number): string => {
    if (temp <= 3000) return '#ffb347'; // Warm orange-yellow
    if (temp <= 4000) return '#ffd699'; // Warm white
    if (temp <= 5000) return '#fff5e6'; // Neutral white
    if (temp <= 5500) return '#ffffff'; // Pure white
    return '#e6f3ff'; // Cool blue-white
  };

  // Get temperature label
  const getTempLabel = (temp: number): string => {
    if (temp <= 3200) return '暖光';
    if (temp <= 4500) return '日光';
    return '冷光';
  };

  const percentage = ((localValue - min) / (max - min)) * 100;
  const thumbColor = getColorForTemp(localValue);

  // Preset values
  const presets = [
    { value: 2700, label: '暖光', icon: Sun, color: '#ffb347' },
    { value: 4000, label: '日光', icon: Sunrise, color: '#ffd699' },
    { value: 6500, label: '冷光', icon: Moon, color: '#e6f3ff' },
  ];

  return (
    <div className="space-y-3">
      {/* Label and Value */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-400">{label}</span>
        {showValue && (
          <div className="flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded-full border border-slate-600"
              style={{ backgroundColor: thumbColor }}
            />
            <span className="text-lg font-bold text-white">
              {localValue}K
            </span>
            <span className="text-sm text-slate-400">
              ({getTempLabel(localValue)})
            </span>
          </div>
        )}
      </div>

      {/* Slider with gradient background */}
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          value={localValue}
          onChange={handleChange}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleTouchEnd}
          disabled={disabled}
          className={`colortemp-slider w-full h-3 rounded-full cursor-pointer appearance-none
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{
            background: `linear-gradient(to right, 
              #ffb347 0%, 
              #ffd699 30%, 
              #ffffff 60%, 
              #e6f3ff 100%)`
          }}
        />
      </div>

      {/* Preset buttons */}
      {showPresets && (
        <div className="flex gap-2">
          {presets.map((preset) => {
            const Icon = preset.icon;
            const isActive = Math.abs(localValue - preset.value) < 200;
            return (
              <button
                key={preset.value}
                onClick={() => {
                  // ✅ 樂觀更新：先更新 UI，再發送命令
                  setLocalValue(preset.value);
                  onChange(preset.value);
                }}
                disabled={disabled}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5
                  ${isActive 
                    ? 'bg-slate-600 text-white' 
                    : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700'}
                  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <Icon className="w-4 h-4" style={{ color: isActive ? preset.color : 'inherit' }} />
                <span>{preset.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Style01_Slider;
